import type { Vendor } from "./types";

// Wave 9.5 — vendor → domain map for logo lookups via Clearbit's free
// Logo API (https://logo.clearbit.com/<domain>). Keys are
// `normalizeVendor`-compatible strings (lowercase, no punctuation, corp
// suffixes stripped). Values are the bare domain, no protocol.
//
// We include a handful of common aliases (e.g. "aws" / "awsamazon" →
// aws.amazon.com) so a user who types "AWS" on an expense still gets a
// recognisable logo rather than the generic amazon.com storefront. The map
// is small enough to audit by eye; add more over time as patterns emerge.
//
// Any vendor NOT in this map falls through to the initial-letter avatar in
// <VendorLogo />. An uploaded `vendor.logoUrl` always wins over both.
export const VENDOR_DOMAINS: Record<string, string> = {
  // Payments / fintech
  stripe: "stripe.com",
  square: "squareup.com",
  paypal: "paypal.com",
  plaid: "plaid.com",
  quickbooks: "quickbooks.intuit.com",
  xero: "xero.com",

  // Cloud / infra
  amazon: "amazon.com",
  aws: "aws.amazon.com",
  awsamazon: "aws.amazon.com",
  "amazon web services": "aws.amazon.com",
  google: "google.com",
  "google cloud": "cloud.google.com",
  gsuite: "workspace.google.com",
  "google workspace": "workspace.google.com",
  workspace: "workspace.google.com",
  microsoft: "microsoft.com",
  azure: "azure.microsoft.com",
  m365: "microsoft365.com",
  "microsoft 365": "microsoft365.com",
  github: "github.com",
  cloudflare: "cloudflare.com",
  vercel: "vercel.com",
  netlify: "netlify.com",
  supabase: "supabase.com",

  // AI
  openai: "openai.com",
  anthropic: "anthropic.com",

  // Collaboration / productivity
  slack: "slack.com",
  zoom: "zoom.us",
  notion: "notion.so",
  figma: "figma.com",
  linear: "linear.app",
  asana: "asana.com",
  jira: "atlassian.com",
  atlassian: "atlassian.com",
  monday: "monday.com",
  airtable: "airtable.com",

  // CRM / support / marketing
  hubspot: "hubspot.com",
  salesforce: "salesforce.com",
  intercom: "intercom.com",
  zendesk: "zendesk.com",
  twilio: "twilio.com",
  sendgrid: "sendgrid.com",
  mailchimp: "mailchimp.com",

  // Ecommerce
  shopify: "shopify.com",

  // Observability
  datadog: "datadoghq.com",
  sentry: "sentry.io",
  posthog: "posthog.com",
  pagerduty: "pagerduty.com",

  // Travel
  uber: "uber.com",
  lyft: "lyft.com",
  delta: "delta.com",
  united: "united.com",
  southwest: "southwest.com",
  hilton: "hilton.com",
  marriott: "marriott.com",
  airbnb: "airbnb.com",

  // Office / retail
  wework: "wework.com",
  regus: "regus.com",
  costco: "costco.com",
  officedepot: "officedepot.com",
  "office depot": "officedepot.com",
  staples: "staples.com",
};

/**
 * Resolve a logo URL for a vendor. Order of precedence:
 *   1. `vendor.logoUrl` (user/org-uploaded override)
 *   2. Clearbit logo for a mapped domain (by normalized name)
 *   3. null — caller should render a fallback avatar
 *
 * Never throws; any unknown vendor returns null.
 */
export function logoUrlForVendor(vendor: Vendor): string | null {
  if (vendor.logoUrl && vendor.logoUrl.trim()) {
    return vendor.logoUrl.trim();
  }
  const key = vendor.normalizedName?.trim();
  if (!key) return null;
  const domain = VENDOR_DOMAINS[key];
  if (!domain) return null;
  return `https://logo.clearbit.com/${domain}`;
}
